// ═══════════════════════════════════════════════════════════════
// Impact — Module Layout with grouped sub-navigation
// ═══════════════════════════════════════════════════════════════

import { Outlet, useLocation } from 'react-router-dom';
import { Group, Tooltip, CopyButton, ActionIcon, Button } from '../../components/gab/GabComponents';
import {
  IconLayoutDashboard, IconList, IconBolt, IconClipboardCheck,
  IconMap, IconStack2, IconWorld, IconCheck, IconCopy,
  IconInbox, IconMicrophone, IconDeviceMobile,
} from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import { useFilteredNav } from '../../hooks/useFilteredNav';
import type { NavItem } from '../../types/nav';
import ModuleNav from '../../components/layout/ModuleNav';
import PageHeader from '../../components/ui/PageHeader';
import { brand } from '../../lib/colors';

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard',    path: '/impact',               icon: IconLayoutDashboard, exact: true },
  { label: 'Assessments',  path: '/impact/assessments',   icon: IconList },
  { label: 'VIDA',         path: '/impact/vida',          icon: IconMicrophone, minLevel: 'editor' },
  { label: 'Quick Capture',path: '/impact/capture',       icon: IconBolt,           minLevel: 'editor' },
  { label: 'Detailed',     path: '/impact/detailed',      icon: IconClipboardCheck, minLevel: 'editor' },
  { label: 'Bulk Entry',   path: '/impact/bulk',          icon: IconStack2,         minLevel: 'editor' },
  { label: 'Map',          path: '/impact/map',           icon: IconMap },
  { label: 'Self-Reports', path: '/impact/self-reports',  icon: IconInbox,          minLevel: 'editor' },
];

export default function ImpactLayout() {
  const { pathname } = useLocation();
  const { activeIncident } = useAuthStore();
  const visibleItems = useFilteredNav('impact', NAV_ITEMS);

  const isDetail = pathname.startsWith('/impact/assessment/');

  const selfReportUrl = activeIncident
    ? `${window.location.origin}/self-report/${activeIncident.id}`
    : '';

  const portalUrl = `${window.location.origin}/impactportal`;

  const extra = (
    <Group gap={4}>
      {activeIncident && (
        <>
          <Tooltip label="Open public damage report portal" withArrow>
            <Button
              variant="light"
              color="#F86A0B"
              size="xs"
              leftSection={<IconWorld size={14} />}
              component="a"
              href={selfReportUrl}
              target="_blank"
            >
              Public Report
            </Button>
          </Tooltip>
          <CopyButton value={selfReportUrl}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? 'Copied!' : 'Copy link'} withArrow>
                <ActionIcon variant="light" color={copied ? 'var(--color-success)' : '#F86A0B'} size="sm" onClick={copy}>
                  {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        </>
      )}
      <Tooltip label="Open field assessor portal" withArrow>
        <Button
          variant="light"
          color="var(--color-primary-700)"
          size="xs"
          leftSection={<IconDeviceMobile size={14} />}
          component="a"
          href={portalUrl}
          target="_blank"
        >
          Field Portal
        </Button>
      </Tooltip>
      <CopyButton value={portalUrl}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? 'Copied!' : 'Copy portal link'} withArrow>
            <ActionIcon variant="light" color={copied ? 'var(--color-success)' : 'var(--color-primary-700)'} size="sm" onClick={copy}>
              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  );

  return (
    <div style={{ padding: '24px' }}>
      {!isDetail && (
        <>
          <PageHeader
            breadcrumbs={[{ label: 'Recover' }, { label: 'Impact Assessment' }]}
            title="Impact Assessment"
            subtitle="Damage documentation, FEMA records and self-report review"
            showIncidentSelector
          />
          <ModuleNav
            items={visibleItems}
            accentColor={brand.blurple}
            extra={extra}
          />
        </>
      )}
      <Outlet />
    </div>
  );
}
